import { z } from "zod";
import { InvoiceStatus, SubscriptionStatus } from "@common/billing";
import {
    FLAT_RATE_PLANS,
    PLAN_CATALOG_VERSION,
    PREMIUM_FEATURES,
} from "@common/flatRateBilling";

export const CardDetailsSchema = z.object({
    brand: z.string(),
    last4: z.string(),
});

export const AddressSchema = z.object({
    street: z.string(),
    city: z.string(),
    postalCode: z.string(),
    state: z.string().nullable(),
    country: z.string(),
});

export const BillingInfoDtoSchema = z.object({
    paymentMethod: CardDetailsSchema.nullable(),
    email: z.string().nullable(),
    address: AddressSchema.nullable(),
    name: z.string().nullable(),
});

const billingIntervalSchema = z.enum(["day", "month", "week", "year"]);

const discountDurationSchema = z.enum(["forever", "once", "repeating"]);

export enum SubscriptionType {
    CUSTOM = "CUSTOM",
    MONTHLY = "MONTHLY",
    FLAT_RATE = "FLAT_RATE",
}

export const SubscriptionDiscountSchema = z.object({
    name: z.string().nullable(),
    duration: discountDurationSchema,
    durationInMonths: z.number().nullable(),
    code: z.string().nullable(),
    amountOff: z.number().nullable(),
    percentOff: z.number().nullable(),
});

export const SubscriptionMeteredPriceSchema = z.object({
    priceId: z.string(),
    productId: z.string(),
    displayName: z.string(),
    priceNickname: z.string().nullable(),
    productName: z.string().nullable(),
    currency: z.string(),
    unitAmountCents: z.number(),
    monthlyAmountCents: z.number(),
    interval: billingIntervalSchema,
    intervalCount: z.number(),
});

/**
 * Per-item snapshot of a V3 licensed (non-metered) subscription item.
 *
 * Persisted on `billing.subscriptionDetails.licensedPriceItems` so the
 * cron quantity dispatcher and the UI can read the current quantities,
 * unit amounts, and current billing period without round-tripping to
 * Stripe on every read.
 *
 * Org V3 subs carry three items (Basic-clients, Plus-clients, add-on
 * entities) all sharing a single monthly period. Workspace V3 subs
 * carry one billing-period item (Basic yearly or Plus quarterly) plus
 * an optional quarterly add-on item; quantities are 1 for the base
 * plan and `entityCount` for the add-on.
 *
 * `stripeItemId` is the Stripe subscription-item id (e.g. `si_abc`),
 * required by the cron quantity dispatcher because `subscriptions.update`
 * addresses items by their subscription-item id (not by price id —
 * supplying a bare `{ price }` to that call would add a duplicate item
 * instead of updating the existing one). Optional so legacy snapshots
 * persisted before this field existed still validate; the cron
 * dispatcher skips them and the next webhook refresh backfills.
 */
export const SubscriptionLicensedPriceItemSchema = z.object({
    stripeItemId: z.string().optional(),
    priceId: z.string(),
    productId: z.string(),
    quantity: z.number().int().nonnegative(),
    // Stripe `unit_amount` is `>= 0` integer cents; bound at the schema
    // level so a malformed snapshot can't silently propagate negative
    // amounts into downstream invoice math (`monthlyPriceCents`, etc.).
    unitAmountCents: z.number().int().nonnegative(),
    billingIntervalMonths: z.number().int().positive(),
    // Unix epoch seconds — never negative for live Stripe data; reject
    // negatives so corrupt snapshots fail validation early.
    currentPeriodStart: z.number().int().nonnegative(),
    currentPeriodEnd: z.number().int().nonnegative(),
});

export const SubscriptionDetailsDtoSchema = z.object({
    type: z.nativeEnum(SubscriptionType),
    status: z.nativeEnum(SubscriptionStatus),
    product: z.string().nullable(),
    price: z.number(),
    monthlyPriceCents: z.number(),
    renewsOn: z.coerce.date().nullable(),
    plan: z.nativeEnum(FLAT_RATE_PLANS),
    cancelsAt: z.coerce.date().nullable(),
    startedAt: z.coerce.date().nullable(),
    cancelOnTrialEnd: z.boolean(),
    periodEndsAt: z.coerce.date().nullable(),
    nextPeriodPlan: z.nativeEnum(FLAT_RATE_PLANS),
    interval: billingIntervalSchema,
    intervalCount: z.number(),
    intervalAmountCents: z.number(),
    discountedIntervalAmountCents: z.number(),
    discountedMonthlyAmountCents: z.number(),
    discount: SubscriptionDiscountSchema.nullable(),
    meteredPrices: z.array(SubscriptionMeteredPriceSchema),
    /**
     * V3 licensed (non-metered) subscription items. Optional so V1/V2
     * metered subs persist with the legacy shape unchanged.
     */
    licensedPriceItems: z.array(SubscriptionLicensedPriceItemSchema).optional(),
});

export type SubscriptionDetailsDto = z.infer<
    typeof SubscriptionDetailsDtoSchema
>;

export const InvoiceDtoSchema = z.object({
    date: z.coerce.date(),
    status: z.nativeEnum(InvoiceStatus),
    url: z.string().nullable(),
    totalInCents: z.number(),
});

export const BillingHistoryDtoSchema = z.object({
    invoices: z.array(InvoiceDtoSchema),
});

export type InvoiceDto = z.infer<typeof InvoiceDtoSchema>;

export const BillingDtoSchema = z.object({
    subscriptionId: z.string().nullable(),
    subscriptionStatus: z.nativeEnum(SubscriptionStatus).nullable(),
    setupIntentId: z.string().nullable(),
    subscribedAt: z.coerce.date().nullable(),
    cancelledAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    subscriptionDetails: SubscriptionDetailsDtoSchema.nullable(),
    planCatalogVersion: z.nativeEnum(PLAN_CATALOG_VERSION).optional(),
});

export type BillingDto = z.infer<typeof BillingDtoSchema>;

export const OrganizationEnrollmentPricingSchema = z.object({
    basicClientMonthlyAmountCents: z.number().int().min(0),
    plusClientMonthlyAmountCents: z.number().int().min(0),
    additionalEntityMonthlyAmountCents: z.number().int().min(0),
});

export type OrganizationEnrollmentPricing = z.infer<
    typeof OrganizationEnrollmentPricingSchema
>;

export const UpcomingInvoiceLineItemSchema = z.object({
    /**
     * Stable UI key: `${product}:${stripePriceId}` for billable lines
     * (including `additionalEntities:${stripePriceId}`), or
     * `complimentary:basic` / `complimentary:plus` /
     * `complimentary:advanced` / `complimentary:additionalEntities`.
     */
    key: z.string().min(1),
    product: z.enum([
        "basic",
        "plus",
        "advanced",
        "additionalEntities",
        "complimentaryBasic",
        "complimentaryPlus",
        "complimentaryAdvanced",
        "complimentaryAdditionalEntities",
        "complimentaryReadOnly",
    ]),
    stripePriceId: z.string().nullable(),
    label: z.string(),
    quantity: z.number().int().min(0),
    // `always_invoice` proration produces negative credit lines on
    // downgrades/removals, so aggregated per-line amounts can be negative.
    unitAmountCents: z.number().int(),
    totalCents: z.number().int(),
});

export type UpcomingInvoiceLineItem = z.infer<
    typeof UpcomingInvoiceLineItemSchema
>;

/**
 * Aggregated upcoming-invoice preview for a V3 org-owned billing.
 *
 * Built from the same path as Stripe quantity reporting
 * (`countBillingMetrics` → `buildDesiredState`) so on-screen quantities
 * match what reconcile would write.
 */
export const UpcomingInvoiceDtoSchema = z.object({
    periodEndsAt: z.coerce.date().nullable(),
    items: z.array(UpcomingInvoiceLineItemSchema),
    // Equals sum of items[].totalCents (catalog unit × qty, AIP applied).
    totalCents: z.number().int(),
    // Remaining Stripe customer invoice credit (`customer.balance`, credit as positive cents).
    creditsBalanceCents: z.number().int().min(0),
    // Lifetime invoice credits granted (sum of credit customer-balance transactions).
    creditsTotalGrantedCents: z.number().int().min(0),
});

export type UpcomingInvoiceDto = z.infer<typeof UpcomingInvoiceDtoSchema>;

/**
 * Schema for GET /flat-rate-billing/:workspaceId/entity-billing-count.
 *
 * Describes how many entities on a Plus V2 workspace have crossed the
 * per-entity transaction threshold and how many are billed as additional.
 */
export const EntityBillingCountResponseSchema = z.object({
    entities: z.array(
        z.object({
            entityId: z.number().int().positive(),
            isExcludedFromBilling: z.boolean(),
            isIncludedInLicense: z.boolean(),
            isPaid: z.boolean(),
        }),
    ),
    additionalEntityMonthlyAmountCents: z.number().int().min(0),
    /** Entities that have hit or exceeded the transaction threshold. */
    qualifiedEntityCount: z.number().int().min(0),
    /** Entity covered by the base plan license. */
    includedInLicenseEntityCount: z.number().int().min(0),
    /**
     * Additional metered entities above the one covered by the base plan.
     * Always >= 0.
     */
    additionalEntityCount: z.number().int().min(0),
    /**
     * The transaction count threshold that triggers entity metering.
     * Surfaced so the UI never needs to hard-code it.
     */
    entityTransactionsThreshold: z.number().int().positive(),
});

export type EntityBillingCountResponse = z.infer<
    typeof EntityBillingCountResponseSchema
>;

export const FlatRatePlanSchema = z.object({
    plan: z.nativeEnum(FLAT_RATE_PLANS),
    features: z.array(z.nativeEnum(PREMIUM_FEATURES)),
    billingInterval: z.number(),
    monthlyAmountCents: z.number(),
    intervalAmountCents: z.number(),
    usageLimit: z.number().nullable(),
});
export type FlatRatePlan = z.infer<typeof FlatRatePlanSchema>;

export const OrganizationPlansResponseSchema = z.object({
    plans: z.array(FlatRatePlanSchema),
    planCatalogVersion: z.nativeEnum(PLAN_CATALOG_VERSION),
});

export const OrganizationOwnedPlanPriceSchema = z.object({
    plan: z.nativeEnum(FLAT_RATE_PLANS),
    monthlyAmountCents: z.number().int().nonnegative(),
    monthlyAmountCentsBeforeDiscount: z.number().int().nonnegative(),
    isComplimentary: z.boolean(),
});
export type OrganizationOwnedPlanPrice = z.infer<
    typeof OrganizationOwnedPlanPriceSchema
>;

export const OrganizationOwnedPlanPricesResponseSchema = z.object({
    plans: z.array(OrganizationOwnedPlanPriceSchema),
    isAipProgramEnrolled: z.boolean(),
    discount: SubscriptionDiscountSchema.nullable(),
});
export type OrganizationOwnedPlanPricesResponse = z.infer<
    typeof OrganizationOwnedPlanPricesResponseSchema
>;

export const calculatePriceResponseSchema = z.object({
    total: z.number(),
    totalBeforeDiscount: z.number(),
    discount: SubscriptionDiscountSchema.nullable(),
    invalidDiscountCode: z.boolean(),
    nextBillingDate: z.string().datetime({ offset: true }),
});
export type CalculatePriceResponse = z.infer<
    typeof calculatePriceResponseSchema
>;
